/* ============================================================================
   atc-tracking.js — site-wide Add-to-Cart SOURCE attribution.

   Goal: know WHERE every add-to-cart came from (collection quick-add, PDP buy
   box, UpCart upsell, cart, etc.) across the whole site — not just the quick
   view modal.

   It transparently OBSERVES every cart add (never alters the request/response)
   by wrapping window.fetch + XMLHttpRequest, detects:
     • POST /cart/add.js            (theme buy boxes, quick add, most apps)
     • Storefront GraphQL cartLinesAdd  (UpCart slide-cart upsells)
   …resolves a SOURCE, and emits ONE normalized event to every analytics sink:
     • GA4   gtag('event','add_to_cart', { items:[{… item_list_name: source}], atc_source })
     • GTM   dataLayer.push({ event:'teeccino_add_to_cart', atc_source, ecommerce })
     • DOM   CustomEvent 'teeccino:add_to_cart'  (for a Custom Pixel / dashboards)
     • Meta  window.fbq('track','AddToCart', …)  (guarded — no-op if no page fbq)
     • POST  window.TEECCINO_ATC_ENDPOINT  (optional log for the custom dashboard;
             unset by default → no POST. DataOps can point this at a collector.)

   SOURCE resolution priority:
     1. explicit override — a surface calls window.TeeccinoATC.setSource('x')
        right before its add (e.g. the quick-add modal). One-shot, TTL'd.
     2. UpCart slide-cart open at add time → 'upcart-upsell'
     3. page/context heuristic → 'pdp' | 'collection' | 'cart' | 'other'

   Hard rule: this wraps fetch/XHR globally, so it must NEVER throw and ALWAYS
   return the original promise/result. Every observation path is try/caught.
   ============================================================================ */
(function () {
  'use strict';

  var SOURCE_TTL_MS = 5000;
  var pendingSource = null; // { source: string, expires: number }

  function nowMs() { return (Date.now ? Date.now() : +new Date()); }

  var API = {
    /* A surface calls this right before its own /cart/add so the next add is
       attributed to it (one-shot, expires so it can't mis-attribute later adds). */
    setSource: function (name, ttlMs) {
      pendingSource = { source: String(name || 'other'), expires: nowMs() + (ttlMs || SOURCE_TTL_MS) };
    },
    /* Manual emit for code that already has rich item data. */
    track: function (payload) { emit(payload || {}); },
    /* Exposed for debugging / dashboards. */
    resolveSource: resolveSource
  };
  window.TeeccinoATC = API;

  function upcartOpen() {
    try {
      var nodes = document.querySelectorAll('[id^="upcart"], [class^="upcart"], [class*=" upcart"], #upcart-container, .upcart-cart-drawer');
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (!n || n.offsetParent === null) continue; // not visible
        var hay = (n.className && n.className.toString ? n.className.toString() : '') + ' ' + (n.id || '');
        if (!/drawer|container|cart|slider|modal|panel/i.test(hay)) continue;
        var r = n.getBoundingClientRect();
        if (r.width > 180 && r.height > 180) return true; // a real open drawer
      }
    } catch (e) {}
    return false;
  }

  function resolveSource() {
    try {
      if (pendingSource && pendingSource.expires > nowMs()) return pendingSource.source;
      if (upcartOpen()) return 'upcart-upsell';
      var p = (window.location && window.location.pathname) || '';
      if (p.indexOf('/products/') > -1) return 'pdp';
      if (p.indexOf('/collections/') > -1) return 'collection';
      if (p === '/cart' || p.indexOf('/cart') === 0) return 'cart';
      return 'other';
    } catch (e) { return 'other'; }
  }

  /* Map a /cart/add.js JSON response to GA4 item objects. The response is either
     a single line object or { items: [ … ] } (multi-add). */
  function itemsFromCartAddJson(json, source) {
    var out = [];
    try {
      var list = (json && json.items && json.items.length) ? json.items : (json && (json.id || json.variant_id) ? [json] : []);
      for (var i = 0; i < list.length; i++) {
        var it = list[i] || {};
        out.push({
          item_id: String(it.product_id || it.id || ''),
          item_name: it.product_title || it.title || '',
          item_variant: it.variant_title || String(it.variant_id || it.id || ''),
          item_category: it.product_type || '',
          price: (typeof it.price === 'number') ? (it.price / 100) : undefined,
          quantity: it.quantity || 1,
          item_list_name: source
        });
      }
    } catch (e) {}
    return out;
  }

  /* Best-effort items from a Storefront GraphQL cartLinesAdd request body
     (UpCart). We only have merchandiseId (variant gid) + quantity in the
     request; price/title are unknown here, which is fine for source counting. */
  function itemsFromGraphqlBody(body, source) {
    var out = [];
    try {
      var data = (typeof body === 'string') ? JSON.parse(body) : body;
      var vars = data && (data.variables || (data[0] && data[0].variables));
      var lines = vars && (vars.lines || vars.cartLines || vars.line);
      if (lines && !lines.length && lines.merchandiseId) lines = [lines];
      if (lines && lines.length) {
        for (var i = 0; i < lines.length; i++) {
          var ln = lines[i] || {};
          var gid = ln.merchandiseId || '';
          var vid = (gid.split && gid.split('/').pop()) || gid;
          out.push({ item_id: String(vid), item_variant: String(vid), quantity: ln.quantity || 1, item_list_name: source });
        }
      }
    } catch (e) {}
    return out;
  }

  function sum(items) {
    var v = 0;
    for (var i = 0; i < items.length; i++) v += (items[i].price || 0) * (items[i].quantity || 1);
    return v;
  }

  function emit(payload) {
    try {
      var source = payload.source || resolveSource();
      var items = payload.items || [];
      var currency = payload.currency ||
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      var value = sum(items);

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'add_to_cart', { currency: currency, value: value, atc_source: source, items: items });
      }
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: 'teeccino_add_to_cart', atc_source: source, ecommerce: { currency: currency, value: value, items: items } });
      }
      /* Meta AddToCart intentionally NOT fired here. The Shopify FB&IG sales
         channel already emits a browser+CAPI-paired AddToCart (shared event_id)
         that dedupes correctly. A theme-side fbq fire has no access to that
         event_id, so it would always be an orphan browser-only event (no CAPI
         twin) -> double-counts + tanks dedup coverage. Removed 2026-06-15. */
      try {
        document.dispatchEvent(new CustomEvent('teeccino:add_to_cart', {
          bubbles: true, detail: { source: source, value: value, currency: currency, items: items }
        }));
      } catch (e) {}

      if (window.TEECCINO_ATC_ENDPOINT) {
        try {
          var body = JSON.stringify({ source: source, value: value, currency: currency, items: items, path: location.pathname });
          if (navigator.sendBeacon) navigator.sendBeacon(window.TEECCINO_ATC_ENDPOINT, body);
          else fetch(window.TEECCINO_ATC_ENDPOINT, { method: 'POST', body: body, keepalive: true, headers: { 'Content-Type': 'application/json' } });
        } catch (e) {}
      }

      pendingSource = null; // one-shot override consumed
    } catch (e) { /* analytics must never break the cart */ }
  }

  function isCartAddUrl(url) { return /\/cart\/add(\.js)?(\?|$)/.test(url || ''); }
  function isGraphqlUrl(url) { return /\/api\/[^?]*graphql/i.test(url || ''); }

  /* ---- Hidden line-item property stamping ----------------------------------
     Inject a hidden `_atc_source` line-item property into every cart add so the
     source rides through to the ORDER (visible in admin order details + the
     order API) — order-level attribution available immediately, no GA4 wait.
     The `_` prefix hides it from cart/checkout display. Handles every common
     /cart/add body shape (FormData, URLSearchParams, JSON, urlencoded string)
     and Storefront cartLinesAdd attributes. Bulletproof: any failure returns the
     ORIGINAL body unchanged so the add can never break. */
  var ATC_PROP = '_atc_source';

  function stampAddBody(body, source) {
    try {
      if (!body || !source) return body;
      var key = 'properties[' + ATC_PROP + ']';
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (!body.has(key)) body.append(key, source);
        return body;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        if (!body.has(key)) body.append(key, source);
        return body;
      }
      if (typeof body === 'string') {
        var s = body.replace(/^\s+/, '');
        if (s.charAt(0) === '{' || s.charAt(0) === '[') {
          var obj = JSON.parse(body);
          var add = function (o) {
            if (!o) return;
            if (o.items && o.items.length) {
              for (var i = 0; i < o.items.length; i++) {
                o.items[i].properties = o.items[i].properties || {};
                if (!(ATC_PROP in o.items[i].properties)) o.items[i].properties[ATC_PROP] = source;
              }
            } else {
              o.properties = o.properties || {};
              if (!(ATC_PROP in o.properties)) o.properties[ATC_PROP] = source;
            }
          };
          add(obj);
          return JSON.stringify(obj);
        }
        if (body.indexOf('properties%5B' + ATC_PROP) === -1 && body.indexOf(key) === -1) {
          return body + (body.length ? '&' : '') + 'properties%5B' + ATC_PROP + '%5D=' + encodeURIComponent(source);
        }
        return body;
      }
    } catch (e) {}
    return body;
  }

  function stampGraphqlBody(body, source) {
    try {
      if (typeof body !== 'string' || !source) return body;
      var data = JSON.parse(body);
      var stampOp = function (op) {
        if (!op || !op.variables) return;
        var lines = op.variables.lines || op.variables.cartLines;
        if (!lines) return;
        if (!lines.length && lines.merchandiseId) lines = [lines];
        for (var i = 0; i < lines.length; i++) {
          lines[i].attributes = lines[i].attributes || [];
          var has = false;
          for (var j = 0; j < lines[i].attributes.length; j++) { if (lines[i].attributes[j] && lines[i].attributes[j].key === ATC_PROP) has = true; }
          if (!has) lines[i].attributes.push({ key: ATC_PROP, value: source });
        }
      };
      if (data && data.length) { for (var k = 0; k < data.length; k++) stampOp(data[k]); } else { stampOp(data); }
      return JSON.stringify(data);
    } catch (e) { return body; }
  }

  /* ---- fetch wrapper (observe-only) ---- */
  if (typeof window.fetch === 'function') {
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '', method = 'GET', body = null, handle = false, gql = false, source = null;
      try {
        url = (typeof input === 'string') ? input : (input && input.url) || '';
        method = (init && init.method) || (input && input.method) || 'GET';
        body = (init && init.body) || null;
        if (isCartAddUrl(url) && /post/i.test(method)) { handle = true; source = resolveSource(); }
        else if (isGraphqlUrl(url) && /post/i.test(method) && typeof body === 'string' && body.indexOf('cartLinesAdd') > -1) {
          handle = true; gql = true; source = upcartOpen() ? 'upcart-upsell' : resolveSource();
        }
        // Stamp the hidden _atc_source line-item property onto the outgoing add.
        // Only when the body lives on `init` (the common fetch(url, {body}) shape);
        // Request-object inputs are left untouched (observe-only) to avoid breakage.
        if (handle && init && ('body' in init)) {
          init.body = gql ? stampGraphqlBody(body, source) : stampAddBody(body, source);
        }
      } catch (e) {}

      var p = _fetch.apply(this, arguments);
      if (handle && p && p.then) {
        try {
          p.then(function (res) {
            try {
              if (gql) { emit({ source: source, items: itemsFromGraphqlBody(body, source) }); return res; }
              res.clone().json().then(function (json) {
                emit({ source: source, items: itemsFromCartAddJson(json, source), currency: json && json.currency });
              })['catch'](function () { emit({ source: source, items: [] }); });
            } catch (e) {}
            return res;
          })['catch'](function () {});
        } catch (e) {}
      }
      return p;
    };
  }

  /* ---- XMLHttpRequest wrapper (observe-only) ---- */
  try {
    var _open = XMLHttpRequest.prototype.open;
    var _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__atcAdd = isCartAddUrl(url) && /post/i.test(method || ''); } catch (e) { this.__atcAdd = false; }
      return _open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this.__atcAdd) {
          var src = resolveSource(), xhr = this;
          // Stamp the hidden _atc_source property onto the outgoing add body.
          if (arguments.length) { arguments[0] = stampAddBody(body, src); }
          this.addEventListener('load', function () {
            try {
              if (xhr.status >= 200 && xhr.status < 300) {
                var json = JSON.parse(xhr.responseText);
                emit({ source: src, items: itemsFromCartAddJson(json, src), currency: json && json.currency });
              }
            } catch (e) {}
          });
        }
      } catch (e) {}
      return _send.apply(this, arguments);
    };
  } catch (e) {}
})();
